import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import AddIcon from "@mui/icons-material/Add";
import TabContext from "@mui/lab/TabContext";
import { TablePagination, Badge, Alert, Button, Dialog, DialogContent, DialogContentText, Divider, FormControl, Grid, InputLabel, List, MenuItem, Select, Tab, Tabs, useMediaQuery, } from "@mui/material";
import { Box } from "@mui/material";
import { CError } from "app/common/CError";
import { Loading } from "app/lib/components/Loading";
import { makeNonEmpty } from "core/util/array";
import { useSecureMessagesByPartyQuery, useReadSecureMessageMutation, SecureMessagesByPartyDocument, usePartyProfileDetailsQuery, UnreadMessagesCountByPartyDocument, } from "generated/generated-models";
import React, { useCallback, useEffect, useState } from "react";
import CreateNewMessage from "./CreateNewMessage";
import MessageChainView from "./MessageChainView";
import MessageOverviewCard from "./MessageOverviewCard";
import MessageSearchBar from "./MessageSearchBar";
import MailIcon from "@mui/icons-material/MailOutlined";
import { SecureMessageContext, SecureMessageSortingType, SecureMessageStatus, } from "./SecureMessagingContext";
import { useSafeArea } from "app/layout/Navbar/useSafeArea";
import { Capacitor } from "@capacitor/core";
const DEFAULT_ROWS_PER_PAGE = 10;
const SecureMessagingView = () => {
    const platform = Capacitor.getPlatform();
    const [openNewMessage, setOpenNewMessage] = useState(false);
    const fullScreen = useMediaQuery("(max-width: 768px)");
    const [messagePool, setMessagePool] = useState([]);
    const [filteredMessages, setFilteredMessages] = useState(messagePool);
    const { data: partyProfileDetails } = usePartyProfileDetailsQuery();
    const partyId = partyProfileDetails?.userSession?.Party?.Id;
    const [handleReading] = useReadSecureMessageMutation();
    const [secureMessageContext, setSecureMessageContext] = useState({
        SelectedMessage: null,
        Filter: {
            Keyword: "",
            MessageStatus: SecureMessageStatus.All,
        },
        Sorting: SecureMessageSortingType.Date,
    });
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [page, setPage] = useState(0);
    const [heldMessageIds, setHeldMessageIds] = useState([]);
    const { insetTop } = useSafeArea();
    const { data: messagesByParty, loading: loadingMessagesByParty, error: errorMessagesByParty, } = useSecureMessagesByPartyQuery({
        fetchPolicy: "network-only",
    });
    useEffect(() => {
        if (!loadingMessagesByParty && messagesByParty) {
            setMessagePool(makeNonEmpty(messagesByParty.secureMessagesByParty?.SecureMessages));
        }
    }, [messagesByParty, loadingMessagesByParty]);
    useEffect(() => {
        if (messagePool) {
            setFilteredMessages(filterMessages(messagePool));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secureMessageContext.Filter]);
    useEffect(() => {
        if (messagePool) {
            const sortedMessages = sortMessages(messagePool);
            setMessagePool(sortedMessages);
            setFilteredMessages(filterMessages(sortedMessages));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messagePool, secureMessageContext.Sorting]);
    function handleChangePage(_event, pageNumber) {
        setPage(pageNumber);
    }
    function handleChangeRowsPerPage(_event) {
        setRowsPerPage(Number(_event.target.value));
    }
    const filterMessages = (unfilteredMessages) => {
        return unfilteredMessages.reduce((messages, message) => {
            if ((secureMessageContext?.Filter.MessageStatus ===
                SecureMessageStatus.All &&
                message.IsArchived) ||
                (secureMessageContext?.Filter.MessageStatus ===
                    SecureMessageStatus.Unread &&
                    message.IsRead &&
                    !heldMessageIds.includes(message.Id)) ||
                (secureMessageContext?.Filter.MessageStatus ===
                    SecureMessageStatus.Archive &&
                    !message.IsArchived) ||
                (secureMessageContext?.Filter.Keyword &&
                    secureMessageContext?.Filter.Keyword.length >= 3 &&
                    !message.ToDoItem.Messages?.some((element) => element?.Text.toLowerCase().includes(secureMessageContext?.Filter.Keyword.toLowerCase())))) {
                return messages;
            }
            messages.push(message);
            return messages;
        }, []);
    };
    const sortMessages = (unsortedMessages) => {
        switch (secureMessageContext.Sorting) {
            case SecureMessageSortingType.Date:
                return unsortedMessages.sort((messageA, messageB) => messageA.ToDoItem.Messages[messageA.ToDoItem.Messages.length - 1]?.CreatedOn <
                    messageB.ToDoItem.Messages[messageB.ToDoItem.Messages.length - 1]?.CreatedOn
                    ? 1
                    : -1);
            case SecureMessageSortingType.AZ:
                return unsortedMessages.sort((messageA, messageB) => messageA.ToDoItem.Text > messageB.ToDoItem.Text ? 1 : -1);
            case SecureMessageSortingType.ZA:
                return unsortedMessages.sort((messageA, messageB) => messageA.ToDoItem.Text < messageB.ToDoItem.Text ? 1 : -1);
            default:
                return unsortedMessages;
        }
    };
    function handleTabChange(_event, newValue) {
        setHeldMessageIds([]);
        setSecureMessageContext({
            ...secureMessageContext,
            Filter: { ...secureMessageContext.Filter, MessageStatus: newValue },
        });
    }
    function handleSortByChange(event) {
        setSecureMessageContext({
            ...secureMessageContext,
            Sorting: SecureMessageSortingType[event.target.value],
        });
    }
    const handleNewMessageClick = useCallback(() => {
        setOpenNewMessage(true);
    }, []);
    const handleCloseNewMessage = useCallback(() => {
        setOpenNewMessage(false);
    }, []);
    function handleCreatedNewMessage(secureMessage) {
        setHeldMessageIds([]);
        setSecureMessageContext({
            ...secureMessageContext,
            Filter: {
                ...secureMessageContext.Filter,
                MessageStatus: SecureMessageStatus.All,
            },
            SelectedMessage: secureMessage.Id === secureMessageContext.SelectedMessage?.Id
                ? null
                : secureMessage,
        });
        setMessagePool([...messagePool, secureMessage]);
    }
    function handleSearchChange(_event) {
        setSecureMessageContext({
            ...secureMessageContext,
            Filter: {
                ...secureMessageContext.Filter,
                Keyword: _event.currentTarget.value.trim(),
            },
        });
    }
    function handleSelectMessage(secureMessage) {
        setSecureMessageContext({
            ...secureMessageContext,
            SelectedMessage: secureMessage.Id === secureMessageContext.SelectedMessage?.Id
                ? null
                : secureMessage,
        });
        if (secureMessage.IsRead !== true) {
            setHeldMessageIds([...heldMessageIds, secureMessage.Id]);
            handleReadingSubmit(secureMessage);
        }
    }
    const handleReadingSubmit = async (secureMessage) => {
        await handleReading({
            variables: {
                input: {
                    SecureMessageId: secureMessage?.Id,
                },
            },
            refetchQueries: [
                { query: SecureMessagesByPartyDocument },
                { query: UnreadMessagesCountByPartyDocument },
            ],
        });
    };
    if (loadingMessagesByParty) {
        return _jsx(Loading, {});
    }
    if (errorMessagesByParty) {
        return _jsx(CError, { error: "Could not retrieve messages" });
    }
    return (_jsxs(_Fragment, { children: [_jsxs("section", { className: "sub-wrap secure-messaging-container", children: [_jsx("div", { className: "list-header", children: _jsx("h2", { children: "Messages" }) }), messagesByParty?.secureMessagesByParty?.SecureMessages !=
                        null &&
                        messagesByParty?.secureMessagesByParty?.SecureMessages
                            ?.length > 0 &&
                        platform !== "web" && (_jsxs(Box, { className: "messageFilters", children: [_jsx(MessageSearchBar, { handleSearchChange: handleSearchChange }), _jsxs(FormControl, { sx: { minWidth: 120 }, size: "small", children: [_jsx(InputLabel, { children: "Sort By" }), _jsx(Select, { value: SecureMessageSortingType[secureMessageContext.Sorting], label: "Sort by", onChange: handleSortByChange, children: Object.keys(SecureMessageSortingType).map((secureMessageSortingType) => {
                                            return (_jsx(MenuItem, { value: secureMessageSortingType, children: SecureMessageSortingType[secureMessageSortingType] }, secureMessageSortingType));
                                        }) })] })] })), _jsx(Box, { sx: { width: "100%" }, children: _jsxs(Grid, { container: true, spacing: 2, sx: { mt: 0, mb: 0 }, children: [_jsx(Grid, { item: true, xs: 3.5, className: "messagesList", sx: { mr: 4 }, children: _jsxs(TabContext, { value: secureMessageContext.Filter.MessageStatus, children: [_jsx(Box, { sx: { mb: 3 }, children: _jsx(Tabs, { onChange: handleTabChange, value: secureMessageContext.Filter
                                                        .MessageStatus, children: Object.keys(SecureMessageStatus).map((secureMessageStatus) => {
                                                        return (_jsx(Tab, { id: `secureMessagingView-tab-${secureMessageStatus}`, label: secureMessageStatus ===
                                                                "Unread" ? (_jsx(Badge, { badgeContent: messagesByParty
                                                                    ?.secureMessagesByParty
                                                                    ?.UnreadMessageCount ??
                                                                    0, color: "error", children: secureMessageStatus })) : (secureMessageStatus), value: SecureMessageStatus[secureMessageStatus] }, secureMessageStatus));
                                                    }) }) }), _jsx(Button, { className: "newMessageBtn", id: "new-secure-message-button", variant: "outlined", fullWidth: true, startIcon: _jsx(AddIcon, {}), onClick: handleNewMessageClick, children: "New Message" }), filteredMessages == null ||
                                                (filteredMessages.length === 0 && (_jsx(Alert, { severity: "info", sx: { mt: 2 }, children: "We didn't find anything to show here." }))), filteredMessages != null &&
                                                filteredMessages.length !== 0 && (_jsx(SecureMessageContext.Provider, { value: secureMessageContext, children: _jsx(List, { sx: {
                                                        px: 0.8,
                                                        py: 0.5,
                                                        mt: 1,
                                                        overflow: "auto",
                                                    }, children: (rowsPerPage > 0
                                                        ? filteredMessages.slice(page * rowsPerPage, page * rowsPerPage +
                                                            rowsPerPage)
                                                        : filteredMessages).map((message) => {
                                                        return (_jsxs(React.Fragment, { children: [_jsx(MessageOverviewCard, { message: message, handleSelectMessage: handleSelectMessage }), _jsx(Divider, {})] }, message.Id));
                                                    }) }) })), messagesByParty?.secureMessagesByParty
                                                ?.SecureMessages != null &&
                                                messagesByParty?.secureMessagesByParty
                                                    ?.SecureMessages?.length > 0 &&
                                                platform !== "web" && (_jsx(Box, { sx: { ml: 4 }, className: "messageFilters", children: _jsx(TablePagination, { component: "div", count: filteredMessages.length, page: page, onPageChange: handleChangePage, rowsPerPage: rowsPerPage, onRowsPerPageChange: handleChangeRowsPerPage, sx: {
                                                        "&::-webkit-scrollbar": {
                                                            width: 0,
                                                        },
                                                        margin: "0px",
                                                        padding: "0px",
                                                    } }) }))] }) }), _jsxs(Grid, { item: true, xs: 7.5, className: "messageViewContainer", children: [messagesByParty?.secureMessagesByParty
                                            ?.SecureMessages != null &&
                                            messagesByParty?.secureMessagesByParty
                                                ?.SecureMessages?.length > 0 &&
                                            platform === "web" && (_jsxs(Box, { sx: { ml: 4 }, className: "messageFilters", children: [_jsx(MessageSearchBar, { handleSearchChange: handleSearchChange }), _jsxs(FormControl, { sx: { minWidth: 120 }, size: "small", children: [_jsx(InputLabel, { children: "Sort By" }), _jsx(Select, { value: SecureMessageSortingType[secureMessageContext
                                                                .Sorting], label: "Sort by", onChange: handleSortByChange, children: Object.keys(SecureMessageSortingType).map((secureMessageSortingType) => {
                                                                return (_jsx(MenuItem, { value: secureMessageSortingType, children: SecureMessageSortingType[secureMessageSortingType] }, secureMessageSortingType));
                                                            }) })] })] })), filteredMessages != null &&
                                            filteredMessages.length !== 0 &&
                                            secureMessageContext.SelectedMessage ==
                                                null && (_jsxs(Box, { sx: { my: 12, textAlign: "center" }, className: "messageNote", children: [_jsx(MailIcon, { sx: { mb: 0.7 } }), _jsx("p", { style: { fontSize: 14 }, children: "Select an item to read" })] })), secureMessageContext.SelectedMessage != null && (_jsx(MessageChainView, { secureMessage: secureMessageContext.SelectedMessage, partyId: partyId })), messagesByParty?.secureMessagesByParty
                                            ?.SecureMessages != null &&
                                            messagesByParty?.secureMessagesByParty
                                                ?.SecureMessages?.length > 0 &&
                                            platform === "web" && (_jsx(Box, { sx: { ml: 4 }, className: "messageFilters", children: _jsx(TablePagination, { component: "div", count: filteredMessages.length, page: page, onPageChange: handleChangePage, rowsPerPage: rowsPerPage, onRowsPerPageChange: handleChangeRowsPerPage, sx: {
                                                    overflowX: "scroll",
                                                    "&::-webkit-scrollbar": {
                                                        width: 0,
                                                    },
                                                    margin: "0px",
                                                    padding: "0px",
                                                } }) }))] })] }) })] }), _jsx(Dialog, { fullScreen: fullScreen, className: "createNewMessage", open: openNewMessage, onClose: handleCloseNewMessage, fullWidth: true, children: _jsx(DialogContent, { sx: { m: 4 }, children: _jsx(DialogContentText, { sx: { paddingTop: `${insetTop}px` }, children: _jsx(CreateNewMessage, { handleCloseNewMessage: handleCloseNewMessage, handleCreatedNewMessage: handleCreatedNewMessage }) }) }) })] }));
};
export { SecureMessagingView };
